import {
    qfsAccessBase,
    qfsAccessBuilder,
    templateLocationTypes,
} from '@qdabra/forms-shared';
import { getParameterByName } from '@qdabra/forms-shared/urlHelpers';
import { allFunctions } from '../../../formsviewer.xpath';
import settingsAccessor from '../../utility/settingsAccessor';
import showLoadFailedError from '../UI/loadFailedError';
import showLoader, { showLoaderText } from '../UI/uiLoader.stack.promise';
import configureControls from '../configureControls';
import engine from '../engine';
import {
    logLevelStringToLevel,
    makeLogHelper,
} from '../helpers/logging.helpers';
import characterLimitSource from '../template/characterLimitSource';
import viewManager from '../viewManager';
import makeCoreBuilder from './buildCore';
import createDataConnections from './createDataConnections';
import createDataSources from './createDataSources';
import makeTemplateLocation from './makeTemplateLocation';
import { isDocumentLocation } from '../../../formView/viewForm.formLocation';

const { LOCATION_TYPE_TEMPLATE_NAME } = templateLocationTypes;

const { Qd, qd } = window;

const { FormsViewer: Fv } = Qd;
const { formsViewer: fv } = qd;

const { perfMark, perfMeasure } = qd.util;

// TODO: pass this in during instantiation
const preloadData = fv.preloadData;
const fvData = Fv.FVData;

// 2023-10-25 - Removed getMessageForError()

function loader(renderTarget) {
    const queryString = window.location.search;
    const userName = preloadData.userName;

    const showQueryLoader = showLoaderText('Querying...');

    const qfsAccessOptions = {
        fvData,
        showLoader: showQueryLoader,
    };

    const createQfsAccess = (options = {}) =>
        qfsAccessBuilder(queryString, { ...qfsAccessOptions, ...options });

    const apiBase = qfsAccessBase(queryString, qfsAccessOptions);

    // including location for now because qfsAccess factory throws error if no location is specified in URL or manually
    const loaderApiAccess = createQfsAccess({
        location: makeTemplateLocation('', LOCATION_TYPE_TEMPLATE_NAME),
    });

    const logLevel = getParameterByName('FVConsoleLogLevel') || null;
    const logging = makeLogHelper(loaderApiAccess, {
        consoleLogLevel: logLevelStringToLevel(logLevel),
    });

    const coreBuilder = makeCoreBuilder(
        logging,
        apiBase,
        preloadData,
        createQfsAccess,
        showQueryLoader,
    );

    configureControls();

    async function performFormInitAsync(core, isNewDocument) {
        logging.logVerbose('Begin form init');

        const { template, mainXml, api, shpAccess } = core;

        const settings = settingsAccessor({
            environmentVariables: preloadData.environmentVariables,
        });

        const xpathEngine = template.getXPathEngine();
        const dataSources = createDataSources(template, mainXml, xpathEngine);
        const dataConnections = createDataConnections(
            template,
            api,
            shpAccess,
            xpathEngine,
            dataSources,
            settings,
        );

        const viewManagerInst = viewManager(
            template,
            renderTarget,
            dataSources,
            logging,
            settings,
        );
        await viewManagerInst.setCurrentView(template.getDefaultView());

        const cls = characterLimitSource(template, dataSources);
        const functions = allFunctions(
            dataSources,
            userName,
            cls,
            viewManagerInst,
            settings,
        );

        xpathEngine.functions = functions;

        logging.logVerbose('Form init - before engine init');

        await engine(
            viewManagerInst,
            template,
            functions,
            renderTarget,
            shpAccess,
            xpathEngine,
            dataConnections,
            dataSources,
            api,
            mainXml,
            cls,
            showLoader,
            isNewDocument,
            logging,
            settings,
        ).initAsync();

        logging.logVerbose('End form init');
    }

    function handleLoadFailedError(err) {
        logging.logError('Error during loading', err);

        // intentionally not returning so that wait overlay can hide
        showLoadFailedError(err, renderTarget);
    }

    const loadForm = async (formLocation) => {
        perfMark('formsviewer_start_load');

        try {
            const core = await coreBuilder.getCoreAsync(formLocation);

            perfMark('formsviewer_core_loaded');

            await performFormInitAsync(
                core,
                // isNewDocument
                !isDocumentLocation(formLocation),
            );
        } catch (err) {
            handleLoadFailedError(err);
        } finally {
            perfMeasure(
                'formsviewer_pre_start',
                'domComplete',
                'formsviewer_start_load',
            );
            perfMeasure(
                'formsviewer_template',
                'formsviewer_start_load',
                'formsviewer_template_loaded',
            );
            perfMeasure(
                'formsviewer_core',
                'formsviewer_start_load',
                'formsviewer_core_loaded',
            );
        }
    };

    return {
        loadForm,
    };
}

export default loader;
